import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";

import Loader from "src/Components/Common/Loader";

import { formatDateRange } from "../TimeOff/TimeOff";
import { getTypeMeta, TimeOffTypeIcon } from "../TimeOff/TimeOffType";

// Alert triangle icon matching the dialog design spec.
function AlertTriangleIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <g clipPath="url(#clip0_6081_37118)">
        <path
          d="M18.0996 14.9929L11.4362 3.33181C11.2909 3.07544 11.0802 2.86219 10.8256 2.71383C10.571 2.56547 10.2816 2.4873 9.98687 2.4873C9.69219 2.4873 9.40278 2.56547 9.14817 2.71383C8.89356 2.86219 8.68286 3.07544 8.53757 3.33181L1.8741 14.9929C1.72724 15.2472 1.65023 15.5359 1.65088 15.8296C1.65153 16.1233 1.72982 16.4116 1.87781 16.6653C2.02579 16.919 2.23822 17.129 2.49355 17.2741C2.74888 17.4193 3.03805 17.4943 3.33174 17.4917H16.6587C16.9509 17.4914 17.238 17.4142 17.491 17.2679C17.744 17.1215 17.9541 16.9112 18.1001 16.658C18.2461 16.4048 18.3229 16.1177 18.3228 15.8254C18.3228 15.5331 18.2458 15.246 18.0996 14.9929Z"
          stroke="#EF4444"
          strokeWidth="1.66587"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M9.99512 7.49634V10.8281" stroke="#EF4444" strokeWidth="1.66587" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9.99512 14.1599H10.0034" stroke="#EF4444" strokeWidth="1.66587" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <defs>
        <clipPath id="clip0_6081_37118">
          <rect width="19.9904" height="19.9904" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

// Confirmation dialog shown before a requester cancels their own pending
// request. `item` is the time-off request to cancel (null = hidden).
function CancelRequestDialog({ item, submitting, onConfirm, onCancel }) {
  if (!item) return null;

  const type = getTypeMeta(item.type);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 px-4">
      <OutsideClickHandler onOutsideClick={() => !submitting && onCancel?.()}>
        <div className="relative flex w-[420px] max-w-full flex-col items-start gap-5 rounded-2xl bg-white p-6 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]">
          <button
            onClick={() => !submitting && onCancel?.()}
            className="absolute right-4 top-4 flex items-center rounded-md p-1 text-[#737373] hover:bg-[#F8FAFC]"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12.6663 4.27325L11.7263 3.33325L7.99967 7.05992L4.27301 3.33325L3.33301 4.27325L7.05967 7.99992L3.33301 11.7266L4.27301 12.6666L7.99967 8.93992L11.7263 12.6666L12.6663 11.7266L8.93967 7.99992L12.6663 4.27325Z"
                fill="currentColor"
              />
            </svg>
          </button>

          <div className="flex w-full flex-col items-start gap-3">
            <div className="flex w-full flex-col items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FEF2F2]">
                <AlertTriangleIcon />
              </div>
              <h3 className="text-[20px] font-InterSemiBold capitalize leading-6 text-[#1A2332]">Cancel Request?</h3>
            </div>
            <p className="text-[16px] font-InterRegular leading-6 text-[#707580]">
              Are you sure you want to cancel this time off request? This action cannot be undone.
            </p>
          </div>

          {/* Summary */}
          <div className="flex w-full flex-col items-start gap-4 rounded-[10px] border border-[#E5E8F3] bg-[#F8F9FE] p-4">
            <div className="flex w-full items-start gap-4">
              <div className="flex flex-1 flex-col items-start gap-1">
                <span className="text-[12px] font-InterSemiBold uppercase leading-[18px] tracking-[0.12px] text-[#7C829A]">
                  Type
                </span>
                <span className="flex items-center gap-1.5 text-[14px] font-InterSemiBold capitalize leading-5 tracking-[0.14px] text-[#1A2332]">
                  <TimeOffTypeIcon type={item.type} size={16} />
                  {type.label}
                </span>
              </div>
              <div className="flex flex-1 flex-col items-start gap-1">
                <span className="text-[12px] font-InterSemiBold uppercase leading-[18px] tracking-[0.12px] text-[#7C829A]">
                  Dates
                </span>
                <span className="text-[14px] font-InterSemiBold capitalize leading-5 tracking-[0.14px] text-[#1A2332]">
                  {formatDateRange(item.startDate, item.endDate)}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex w-full items-start gap-4 pt-2">
            <button
              type="button"
              onClick={() => !submitting && onCancel?.()}
              disabled={submitting}
              className="flex h-10 flex-1 items-center justify-center rounded-lg border border-[#C9CEE2] bg-white text-[16px] font-InterSemiBold capitalize leading-6 text-[#1A2332] shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:bg-[#F8FAFC] disabled:opacity-50"
            >
              Keep Request
            </button>
            <button
              type="button"
              onClick={() => onConfirm?.()}
              disabled={submitting}
              className="flex h-10 flex-1 items-center justify-center rounded-lg bg-[#EF4444] text-[16px] font-InterSemiBold capitalize leading-6 text-white hover:bg-[#DC2626] disabled:opacity-60"
            >
              {submitting ? <Loader variant="button" ariaLabel="cancelling-request" /> : "Cancel Request"}
            </button>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
}

export default CancelRequestDialog;
